import {
    ApiContentSection,
    ApiSectionType,
    INLINE_CTA_ACTION,
} from '@storefronts/common-types'
import styled from '@emotion/styled'
import { useContext, useEffect, useRef } from 'react'
import { PageContext } from '../../../context/PageContextProvider'
import { useLandingPageLinksForHashes } from '../../../hooks/useLandingPageLinksForHashes'
import { useFullLandingPageLink } from '../../../hooks/useFullLandingPageLink'
import { inScrollingDesktop } from '../../../style/scrollingLayout'
import { Constants } from '../../../style/styleConstants'
import { InlineProductPageContent } from '../full-screen/ProductDrawer'
import { SectionContainer } from './Shared'

/** Separates the product from whatever section it follows. */
const INLINE_PRODUCT_TOP_GAP_PX = 16

const InlineProductContainer = styled(SectionContainer)(
    inScrollingDesktop({ paddingTop: INLINE_PRODUCT_TOP_GAP_PX }),
)

type InlineProductPageSectionProps = Extract<
    ApiContentSection,
    { type: ApiSectionType.INLINE_PDP }
>

export const InlineProductPageSection: React.FC<{
    section: InlineProductPageSectionProps
    prioritizeHeroImage?: boolean
}> = ({ section, prioritizeHeroImage = true }) => {
    const resolvedLinks = useLandingPageLinksForHashes([section.linkHash])
    const lpLink = useFullLandingPageLink(resolvedLinks[0])
    const { registerInlineProductSection } = useContext(PageContext)
    const sectionRef = useRef<HTMLDivElement>(null)

    // Register this Inline Product section so it can be scrolled to from the Buy Now button.
    // We include lpLink in dependencies because the SectionContainer (and thus sectionRef.current)
    // is only rendered after lpLink is loaded.
    useEffect(() => {
        if (!sectionRef.current || !section.linkHash) {
            return
        }
        const unregister = registerInlineProductSection(
            sectionRef.current,
            section.linkHash,
        )
        return () => {
            unregister()
        }
    }, [section.linkHash, registerInlineProductSection, lpLink])

    if (!lpLink) return null
    return (
        <InlineProductContainer
            ref={sectionRef}
            paddingTop={0}
            paddingBottom={Constants.SECTION_BASE_SPACING_PX}
        >
            <InlineProductPageContent
                lpLink={lpLink}
                embedded
                showBodyContent={section.includePDPContent || false}
                showReviews={section.includeReviews || false}
                inlineCTAActions={
                    section.actions && section.actions.length > 0
                        ? section.actions
                        : [INLINE_CTA_ACTION.ADD_TO_CART]
                }
                inlineButtonText={section.buttonText}
                prioritizeHeroImage={prioritizeHeroImage}
                bodyBadgesOverride={section.bodyBadges}
                showBrandChat={section.showBrandChat || false}
            />
        </InlineProductContainer>
    )
}
